import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, MousePointerClick, TrendingDown, CheckCircle2, X, Sparkles, RefreshCw, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FunnelAnalyticsTab() {
  const [funnels, setFunnels] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hours, setHours] = useState(24);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [f, c, a] = await Promise.all([
        axios.get(`${API}/admin/analytics/funnels?hours=${hours}`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/clicks?hours=${hours}`, { withCredentials: true }),
        axios.get(`${API}/admin/analytics/alerts?status=open`, { withCredentials: true }),
      ]);
      setFunnels(f.data);
      setClicks(c.data);
      setAlerts(a.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [hours]); // eslint-disable-line
  useEffect(() => {
    const int = setInterval(fetchAll, 30000);
    return () => clearInterval(int);
  }, [hours]); // eslint-disable-line

  async function alertAction(alertId, action, extra = {}) {
    try {
      await axios.post(`${API}/admin/analytics/alerts/${alertId}/action`, { action, ...extra }, { withCredentials: true });
      toast.success(action === "resolve" ? "Alert risolto" : action === "dismiss" ? "Alert ignorato" : action === "apply_suggestion" ? "Soluzione applicata" : "Alert aggiornato");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  }

  if (loading && !funnels) {
    return <div className="flex items-center gap-2 text-zinc-500 p-8"><Loader2 className="w-4 h-4 animate-spin" />Caricamento...</div>;
  }

  return (
    <div className="space-y-6" data-testid="funnel-analytics-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-zinc-900">Funnel & Alert intelligenti</h3>
          <p className="text-sm text-zinc-500">Drop-off detection automatico + suggerimenti UX AI-generated. Scansione ogni 15 min.</p>
        </div>
        <div className="flex items-center gap-2">
          {[24, 48, 168].map((h) => (
            <button
              key={h}
              onClick={() => setHours(h)}
              data-testid={`funnel-hours-${h}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${hours === h ? "bg-[#163321] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            >{h}h</button>
          ))}
          <button onClick={fetchAll} className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600" title="Aggiorna">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* ALERT ATTIVI */}
      {alerts?.items?.length > 0 && (
        <section className="bg-rose-50 border border-rose-200 rounded-2xl p-4 md:p-5" data-testid="alerts-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
            <h4 className="font-semibold text-rose-900">{alerts.items.length} alert aperti</h4>
            <span className="ml-auto text-xs text-rose-600">Totali: open {alerts.stats.open} · in corso {alerts.stats.in_progress} · risolti {alerts.stats.resolved}</span>
          </div>
          <div className="space-y-3">
            {alerts.items.map((a) => (
              <AlertCard key={a.id} alert={a} onAction={alertAction} />
            ))}
          </div>
        </section>
      )}
      {alerts?.items?.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-center gap-3 text-emerald-800">
          <CheckCircle2 className="w-5 h-5" />
          <div>
            <p className="font-semibold">Nessun alert attivo</p>
            <p className="text-xs text-emerald-700">Tutti i funnel performano entro le soglie configurate.</p>
          </div>
        </div>
      )}

      {/* FUNNEL */}
      <section className="bg-white rounded-2xl border border-zinc-200 p-4 md:p-5" data-testid="funnels-section">
        <div className="flex items-center gap-2 mb-4">
          <TrendingDown className="w-4 h-4 text-[#163321]" />
          <h4 className="font-semibold text-zinc-900">Funnel di conversione</h4>
        </div>
        <div className="space-y-5">
          {funnels?.funnels?.map((f) => (
            <FunnelRow key={f.funnel_id} funnel={f} />
          ))}
        </div>
      </section>

      {/* TOP CLICKS */}
      <section className="bg-white rounded-2xl border border-zinc-200 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-4">
          <MousePointerClick className="w-4 h-4 text-[#D97B58]" />
          <h4 className="font-semibold text-zinc-900">Elementi più cliccati ({hours}h)</h4>
        </div>
        <table className="w-full text-sm" data-testid="top-clicks-table">
          <thead>
            <tr className="text-left text-xs text-zinc-500 uppercase tracking-wider border-b border-zinc-100">
              <th className="py-2 font-semibold">Label</th>
              <th className="py-2 font-semibold text-right">Click</th>
              <th className="py-2 font-semibold text-right">Utenti unici</th>
              <th className="py-2 font-semibold">Pagine</th>
            </tr>
          </thead>
          <tbody>
            {clicks?.items?.map((c, i) => (
              <tr key={i} className="border-b border-zinc-50 hover:bg-zinc-50/50">
                <td className="py-2 font-mono text-xs">{c.label}</td>
                <td className="py-2 text-right tabular-nums font-semibold">{c.count}</td>
                <td className="py-2 text-right tabular-nums text-zinc-500">{c.uniq}</td>
                <td className="py-2 text-xs text-zinc-500 truncate" title={c.paths?.join(", ")}>
                  {(c.paths || []).slice(0, 2).join(", ")}
                </td>
              </tr>
            ))}
            {(!clicks?.items || clicks.items.length === 0) && (
              <tr><td colSpan={4} className="py-6 text-center text-zinc-400 italic">Nessun click tracciato nelle ultime {hours}h.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function FunnelRow({ funnel }) {
  const maxSessions = Math.max(...funnel.steps.map((s) => s.sessions), 1);
  const convPct = Math.round((funnel.conversion || 0) * 100);
  return (
    <div data-testid={`funnel-${funnel.funnel_id}`}>
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h5 className="font-semibold text-zinc-900">{funnel.label}</h5>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-zinc-500">Conversion: <strong className={convPct > 30 ? "text-emerald-600" : convPct > 10 ? "text-amber-600" : "text-rose-600"}>{convPct}%</strong></span>
          {funnel.bottleneck && (
            <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[10px] font-semibold">
              Bottleneck: {Math.round(funnel.bottleneck.drop_pct * 100)}% drop
            </span>
          )}
        </div>
      </div>
      <div className="space-y-1">
        {funnel.steps.map((s, i) => {
          const pct = (s.sessions / maxSessions) * 100;
          const prev = i > 0 ? funnel.steps[i - 1].sessions : null;
          const dropPct = prev && prev > 0 ? Math.round(((prev - s.sessions) / prev) * 100) : 0;
          const isBottleneck = funnel.bottleneck && funnel.bottleneck.to === s.step;
          return (
            <div key={s.step} className="flex items-center gap-3">
              <span className="w-32 text-xs text-zinc-600 truncate font-mono" title={s.step}>{s.step}</span>
              <div className="flex-1 bg-zinc-100 rounded-full h-5 relative overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${isBottleneck ? "bg-rose-400" : "bg-[#163321]"}`}
                  style={{ width: `${Math.max(pct, 2)}%` }}
                />
                <span className="absolute inset-0 flex items-center justify-end pr-2 text-[10px] font-semibold text-zinc-700">
                  {s.sessions}
                </span>
              </div>
              {i > 0 && prev > 0 && (
                <span className={`text-[11px] tabular-nums w-14 text-right ${dropPct > 55 ? "text-rose-600 font-bold" : "text-zinc-400"}`}>
                  {dropPct > 0 ? `-${dropPct}%` : "0%"}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AlertCard({ alert, onAction }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const sev = alert.severity || "medium";
  const sevColor = sev === "high" ? "bg-rose-600 text-white" : "bg-amber-500 text-white";

  return (
    <div className="bg-white rounded-xl border border-rose-200 p-4" data-testid={`alert-card-${alert.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${sevColor}`}>{sev}</span>
            <span className="text-[10px] text-zinc-400 font-mono">{alert.kind}</span>
            <span className="text-[10px] text-zinc-400">· {new Date(alert.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <h5 className="font-semibold text-zinc-900">{alert.title}</h5>
          <p className="text-sm text-zinc-600 mt-0.5">{alert.summary}</p>
        </div>
        <button
          onClick={() => onAction(alert.id, "dismiss")}
          className="p-1.5 rounded-lg hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 shrink-0"
          title="Ignora"
          data-testid={`alert-dismiss-${alert.id}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* AI Suggestions */}
      {alert.ai_suggestions?.length > 0 && (
        <div className="mt-3 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1">
            <Sparkles className="w-3 h-3 text-[#D97B58]" /> Soluzioni proposte
          </p>
          {alert.ai_suggestions.map((s, i) => (
            <div key={i} className="flex items-start gap-2 p-2.5 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-zinc-900">{s.title}</p>
                <p className="text-xs text-zinc-600 mt-0.5">{s.desc}</p>
              </div>
              <button
                onClick={() => onAction(alert.id, "apply_suggestion", { applied_suggestion_index: i })}
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#163321] text-white hover:bg-[#0f2217] transition"
                data-testid={`alert-apply-${alert.id}-${i}`}
              >
                <Zap className="w-3 h-3" />
                Applica
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Azioni */}
      <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-zinc-100">
        {!noteOpen && (
          <>
            <button
              onClick={() => setNoteOpen(true)}
              className="text-xs text-zinc-600 hover:text-zinc-900 px-3 py-1.5 rounded-lg hover:bg-zinc-100"
              data-testid={`alert-resolve-btn-${alert.id}`}
            >
              Segna come risolto
            </button>
          </>
        )}
        {noteOpen && (
          <div className="flex items-center gap-2 w-full">
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Nota di risoluzione (opzionale)"
              className="flex-1 text-sm border border-zinc-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#163321]/30"
              maxLength={600}
            />
            <button
              onClick={() => { onAction(alert.id, "resolve", { resolution_note: note }); setNoteOpen(false); setNote(""); }}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Conferma
            </button>
            <button onClick={() => { setNoteOpen(false); setNote(""); }} className="text-xs text-zinc-500 px-2">Annulla</button>
          </div>
        )}
      </div>
    </div>
  );
}
